import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardContent } from '../../../components/ui/Card';
import SimpleChart from '../../../components/analytics/SimpleChart';
import PieChart from '../../../components/analytics/PieChart';
import StackedBarChart from '../../../components/analytics/StackedBarChart';
import MultiLineChart from '../../../components/analytics/MultiLineChart';
import { analyticsService, type ComprehensiveAnalytics } from '../../../services/analyticsService';
import { creatorService } from '../../../services/creatorService';

const AnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<ComprehensiveAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodDays, setPeriodDays] = useState(30);
  const [creatorId, setCreatorId] = useState<string | null>(null);

  useEffect(() => {
    loadCreatorProfile();
  }, []);

  useEffect(() => {
    if (creatorId) {
      loadAnalytics();
    }
  }, [creatorId, periodDays]);

  const loadCreatorProfile = async () => {
    try {
      const res = await creatorService.getMyCreatorProfile();
      if (res?.id) {
        setCreatorId(res.id);
      }
    } catch (error) {
      console.error('Failed to load creator profile:', error);
    }
  };

  const loadAnalytics = async () => {
    if (!creatorId) return;
    try {
      setLoading(true);
      const res = await analyticsService.getAnalytics(creatorId, periodDays);
      setAnalytics(res.analytics);
    } catch (error: any) {
      console.error('Failed to load analytics:', error);
      alert(error?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No analytics data</h3>
        <p className="text-gray-600">Analytics data will appear here once you have activity</p>
      </div>
    );
  }
  // ---- Normalization Layer ----
  // The backend currently returns camelCase keys: conversionFunnel, revenueForecast, memberLTV
  // This page was originally written for shorter keys: conversion, forecast, ltv.
  // We adapt the shape here to keep the presentation component stable.
  const conversion: any = (analytics as any).conversion || (() => {
    const f = (analytics as any).conversionFunnel;
    if (!f) return null;
    const visitors = f.visitors ?? 0;
    const followers = f.followers ?? 0; // treat as signups
    const initiations = f.membershipInitiations ?? 0; // treat as tier selections
    const completed = f.completedMemberships ?? 0;
    const pct = (num: number, den: number) => (den > 0 ? (num / den) * 100 : 0);
    return {
      visitors,
      signups: followers,
      tierSelections: initiations,
      completedMemberships: completed,
      conversionRates: {
        signup: pct(followers, visitors),
        tierSelection: pct(initiations, followers || visitors),
        completion: pct(completed, visitors),
      },
    };
  })();

  const forecast: any = (analytics as any).forecast || (() => {
    const rf = (analytics as any).revenueForecast;
    if (!rf) return null;
    const months = (rf.forecast || []).map((m: any) => ({
      month: m.month,
      projectedRevenue: m.projected ?? 0,
      confidence: typeof m.confidence === 'number' ? m.confidence : 0,
    }));
    const totalProjectedRevenue = months.reduce((sum: number, m: any) => sum + (m.projectedRevenue || 0), 0);
    return { months, totalProjectedRevenue };
  })();

  const ltv: any = (analytics as any).ltv || (() => {
    const ml = (analytics as any).memberLTV;
    if (!ml) return null;
    // Derive min/max from distribution ranges when possible
    let minLTV = 0;
    let maxLTV = 0;
    if (Array.isArray(ml.distribution) && ml.distribution.length > 0) {
      const parseRange = (r: string): [number, number] => {
        const parts = String(r).split('-').map((x) => parseFloat(x.replace(/[^0-9.]/g, '')));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return [parts[0], parts[1]];
        const val = parts[0];
        return [isNaN(val) ? 0 : val, isNaN(val) ? 0 : val];
      };
      const mins: number[] = [];
      const maxs: number[] = [];
      ml.distribution.forEach((d: any) => {
        const [lo, hi] = parseRange(d.range ?? '0-0');
        mins.push(lo);
        maxs.push(hi);
      });
      minLTV = Math.min(...mins);
      maxLTV = Math.max(...maxs);
    }
    const byTier = Array.isArray(ml.distribution)
      ? ml.distribution.map((d: any) => ({
          tierName: d.range ?? 'Tier',
          averageLTV: (ml.averageLTV ?? 0) * (d.percentage ?? 0) / 100,
        }))
      : [];
    return {
      averageLTV: ml.averageLTV ?? 0,
      minLTV,
      maxLTV,
      byTier,
    };
  })();
  // ---- End Normalization Layer ----

  const retentionData = analytics.retention.monthly.map((m) => ({
    label: m.month,
    value: m.retentionRate,
    color: '#f97316',
  }));
  const forecastData = (forecast?.months || []).map((m: any) => ({
    label: m.month,
    value: m.projectedRevenue,
    color: '#10b981',
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Comprehensive analytics and insights</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patreon-orange focus:border-transparent"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Churn Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <ArrowTrendingDownIcon className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-sm text-gray-500">Churn Rate</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{analytics.churnRate.churnRate.toFixed(2)}%</h3>
            <p className="text-sm text-gray-600 mt-1">
              {analytics.churnRate.cancelled} cancelled / {analytics.churnRate.active} active
            </p>
          </CardContent>
        </Card>

        {/* Retention Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <ArrowTrendingUpIcon className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Retention Rate</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">{analytics.churnRate.retentionRate.toFixed(2)}%</h3>
            <p className="text-sm text-gray-600 mt-1">
              Average: {analytics.retention.averageRetentionRate.toFixed(2)}%
            </p>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <UserGroupIcon className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Conversion</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {(conversion?.conversionRates?.completion ?? 0).toFixed(2)}%
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {conversion?.completedMemberships?.toLocaleString?.() ?? 0} / {conversion?.visitors?.toLocaleString?.() ?? 0} visitors
            </p>
          </CardContent>
        </Card>

        {/* Average LTV */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-gray-500">Avg LTV</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              ${Number(ltv?.averageLTV ?? 0).toFixed(2)}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Range: ${Number(ltv?.minLTV ?? 0).toFixed(2)} - ${Number(ltv?.maxLTV ?? 0).toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Retention Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Monthly Retention Rate</h3>
          </CardHeader>
          <CardContent>
            <SimpleChart data={retentionData} type="line" height={250} />
          </CardContent>
        </Card>

        {/* Revenue Forecast */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Revenue Forecast</h3>
          </CardHeader>
          <CardContent>
            <SimpleChart data={forecastData} type="area" height={250} />
            <div className="mt-4 text-sm text-gray-600">
              Total Projected: ${Number(forecast?.totalProjectedRevenue ?? 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">Conversion Funnel</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Visitors</span>
                  <span className="text-sm font-medium text-gray-900">
                    {conversion?.visitors?.toLocaleString?.() ?? 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-patreon-orange h-2 rounded-full" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Signups</span>
                  <span className="text-sm font-medium text-gray-900">
                    {conversion?.signups?.toLocaleString?.() ?? 0} ({Number(conversion?.conversionRates?.signup ?? 0).toFixed(2)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{
                      width: `${conversion?.conversionRates?.signup ?? 0}%`,
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Tier Selections</span>
                  <span className="text-sm font-medium text-gray-900">
                    {conversion?.tierSelections?.toLocaleString?.() ?? 0} ({Number(conversion?.conversionRates?.tierSelection ?? 0).toFixed(2)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{
                      width: `${conversion?.conversionRates?.tierSelection ?? 0}%`,
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Completed</span>
                  <span className="text-sm font-medium text-gray-900">
                    {conversion?.completedMemberships?.toLocaleString?.() ?? 0} ({Number(conversion?.conversionRates?.completion ?? 0).toFixed(2)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{
                      width: `${conversion?.conversionRates?.completion ?? 0}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* LTV by Tier */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900">LTV by Tier</h3>
          </CardHeader>
          <CardContent>
            <SimpleChart
              data={(ltv?.byTier || []).map((tier: any) => ({
                label: tier.tierName,
                value: tier.averageLTV,
                color: '#8b5cf6',
              }))}
              type="bar"
              height={250}
            />
          </CardContent>
        </Card>
      </div>

      {/* Advanced Visualizations */}
      <div className="mt-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Advanced Analytics</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Retention vs Churn Pie Chart */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Retention vs Churn</h3>
            </CardHeader>
            <CardContent>
              <PieChart
                data={[
                  {
                    label: 'Retained',
                    value: analytics.retention.totalRetained,
                    color: '#10b981',
                  },
                  {
                    label: 'Churned',
                    value: analytics.retention.totalChurned,
                    color: '#ef4444',
                  },
                ]}
                size={300}
                innerRadius={0.6}
                showPercentages={true}
              />
            </CardContent>
          </Card>

          {/* Conversion Funnel Distribution */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Funnel Stage Distribution</h3>
            </CardHeader>
            <CardContent>
              <PieChart
                data={[
                  {
                    label: 'Drop-off after Visit',
                    value: (conversion?.visitors ?? 0) - (conversion?.signups ?? 0),
                    color: '#ef4444',
                  },
                  {
                    label: 'Drop-off after Signup',
                    value: (conversion?.signups ?? 0) - (conversion?.tierSelections ?? 0),
                    color: '#f59e0b',
                  },
                  {
                    label: 'Drop-off after Tier Selection',
                    value: (conversion?.tierSelections ?? 0) - (conversion?.completedMemberships ?? 0),
                    color: '#eab308',
                  },
                  {
                    label: 'Completed',
                    value: conversion?.completedMemberships ?? 0,
                    color: '#10b981',
                  },
                ]}
                size={300}
                showPercentages={true}
              />
            </CardContent>
          </Card>

          {/* Monthly Retention Trends (Stacked) */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Monthly Retention Breakdown</h3>
            </CardHeader>
            <CardContent>
              <StackedBarChart
                data={analytics.retention.monthly.map((m) => ({
                  label: m.month,
                  segments: [
                    {
                      name: 'Retained',
                      value: m.retained,
                      color: '#10b981',
                    },
                    {
                      name: 'Churned',
                      value: m.churned,
                      color: '#ef4444',
                    },
                  ],
                }))}
                height={300}
                showValues={true}
              />
            </CardContent>
          </Card>

          {/* Revenue vs Retention Trend */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Revenue & Retention Trends</h3>
            </CardHeader>
            <CardContent>
              <MultiLineChart
                data={(forecast?.months || []).slice(0, analytics.retention.monthly.length).map((m: any, idx: number) => ({
                  label: m.month,
                  values: {
                    'Projected Revenue': m.projectedRevenue,
                    'Retention Rate': analytics.retention.monthly[idx]?.retentionRate || 0,
                  },
                }))}
                series={[
                  { name: 'Projected Revenue', color: '#10b981' },
                  { name: 'Retention Rate', color: '#f97316' },
                ]}
                height={300}
                showDots={true}
              />
            </CardContent>
          </Card>

          {/* LTV Distribution by Tier (Donut) */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">LTV Distribution by Tier</h3>
            </CardHeader>
            <CardContent>
              <PieChart
                data={(ltv?.byTier || []).map((tier: any, idx: number) => ({
                  label: tier.tierName,
                  value: tier.averageLTV,
                  color: ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1'][idx % 6],
                }))}
                size={300}
                innerRadius={0.5}
                showPercentages={true}
              />
            </CardContent>
          </Card>

          {/* Forecast Confidence Visualization */}
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold text-gray-900">Revenue Forecast Confidence</h3>
            </CardHeader>
            <CardContent>
              <MultiLineChart
                data={(forecast?.months || []).map((m: any) => ({
                  label: m.month,
                  values: {
                    'Revenue': m.projectedRevenue,
                    'Confidence': m.confidence,
                  },
                }))}
                series={[
                  { name: 'Revenue', color: '#10b981' },
                  { name: 'Confidence', color: '#3b82f6' },
                ]}
                height={300}
                showDots={true}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
